import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { assertSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { useRef } from "react";
import { useEvent } from "../context/EventProvider";
import type { Answers } from "./answers";
import {
  fetchMe,
  fetchParticipation,
  participationChanged,
  saveParticipation,
  type Me,
  type Recorded,
  type WriteProgress,
} from "./participant";
import { fetchSelection, type Article, type Selection } from "./articles";
import { forgetDraft } from "./draft";
import { restampEntry } from "./answerSession";
import {
  participationRead,
  participationWritten,
  type ParticipationScope,
} from "./participationGone";

/**
 * ═══ LA COMMANDE ENREGISTRÉE, SI LE CACHE LA CONNAÎT ENCORE ═══
 *
 * Elle sert d'indice à l'écriture : identique à celle qu'on s'apprête à écrire, la route
 * n'est pas appelée du tout (voir `sameSelection`). `undefined` veut donc dire « écris,
 * on ne sait pas » — et c'est le repli sûr, puisque la route détruit avant de recréer.
 *
 * ⚠️ ET PÉRIMÉE, ELLE NE PROUVE PLUS RIEN. Une réponse invalidée — par l'écriture d'avant,
 * dans ce même parcours — est celle d'AVANT cette écriture : s'en servir pour conclure
 * « rien n'a changé » ferait sauter une écriture qui, elle, change quelque chose. On
 * l'ignore alors, et on écrit.
 *
 * Hors du composant pour être vérifiable seule : c'est une règle, pas un rendu.
 */
export function knownOrder(
  client: QueryClient,
  key: QueryKey,
): Selection | undefined {
  const state = client.getQueryState<Selection>(key);
  return state && !state.isInvalidated ? state.data : undefined;
}

/**
 * La typologie a-t-elle changé ? La règle du serveur (`typoChanged`, dans
 * `api-v1/api/helpers/cartOnTypoChange.js`) : les deux connues, et différentes. Une
 * première inscription n'en change pas, elle en pose une.
 */
function typoChanged(
  before: number | null | undefined,
  after: number | null | undefined,
): boolean {
  return (
    typeof before === "number" && typeof after === "number" && before !== after
  );
}

/**
 * ═══ LE CACHE, REMIS D'ACCORD AVEC CE QUI VIENT D'ÊTRE ÉCRIT ═══
 *
 * ⚠️ CE QUE L'ÉCRITURE REND SE POSE, ET NE SE REDEMANDE PAS. La route d'écriture répond
 * exactement ce que la lecture répond (voir la fin de `saveParticipation`) : l'enregistré
 * est donc POSÉ dans le cache au lieu d'être relu — un aller-retour de moins, et c'en
 * était un long (six jointures), attendu derrière le « Enregistrement… » de la
 * confirmation, à chaque inscription.
 *
 * ⚠️ POSÉ, DONC PLUS FRAIS QU'UNE RELECTURE, et c'est ce qui rend l'économie sûre : ce qui
 * revient est l'état d'APRÈS cette écriture-ci. Rien à poser — la réponse ne portait pas
 * de participation — et on relit, comme avant : mieux vaut une requête de plus qu'un
 * écran qui rend compte d'après une supposition.
 *
 * ⚠️ ET LE SOLDE AVEC, parce qu'il est calculé sur LA COMMANDE ENREGISTRÉE. Il se lit sous
 * une clé qui ne porte que la sélection demandée — vide, à la confirmation, puisqu'on y
 * facture ce qui est en base : cette clé-là ne change donc JAMAIS, et le cache resservait
 * le montant d'avant. Mesuré : ajouter un article de 990 € à la billetterie, continuer
 * jusqu'à la confirmation, et n'y voir ni solde ni bouton de paiement. Le badge lit le
 * même solde pour savoir s'il est dû, donc il mentait de la même façon.
 *
 * ⚠️ ET LA COMMANDE RETENUE, parce que `saveParticipation` l'écrit aussi. Non périmée,
 * l'étape billetterie rouverte plus tard — au rechargement, ou depuis le menu, quand
 * l'état de navigation ne la masque plus — recochait l'ANCIENNE sélection.
 *
 * ⚠️ ET LES CONFÉRENCES, VIDÉES ET PAS SEULEMENT PÉRIMÉES, QUAND LA TYPOLOGIE CHANGE
 * (bug #238). Le serveur rend alors les places d'une typologie qui n'a plus l'étape
 * (`api-v1/api/helpers/cartOnTypoChange.js`). Périmée, la liste resterait affichée le
 * temps de sa relecture, la séance rendue avec ; et revenu sous une typologie qui a
 * l'étape dans les 30 secondes de `useConferences`, elle reviendrait cochée. Rien
 * n'est rendu par l'écriture, et on ne suppose rien : vidées aussi. Typologie
 * inchangée, on n'y touche pas, et l'étape qu'on vient de quitter ne se redemande pas.
 *
 * ⚠️ ET RIEN DE TOUT CELA NE PEUT FAIRE ÉCHOUER L'ÉCRITURE, qui, elle, a réussi :
 * annoncer « votre réponse n'a pas pu être enregistrée » parce qu'on n'a pas su ranger un
 * cache serait faux, et enverrait réécrire ce qui est déjà en base.
 */
export function settleWrite(
  client: QueryClient,
  key: QueryKey,
  written: Recorded | undefined,
): Promise<void> {
  // Lue AVANT de poser l'écriture : c'est la typologie que le serveur détenait.
  const before = client.getQueryData<Recorded>(key)?.typoId;
  const typoMoved = !written || typoChanged(before, written.typoId);
  if (written) client.setQueryData(key, written);
  return Promise.all([
    ...(written ? [] : [client.invalidateQueries({ queryKey: key })]),
    client.invalidateQueries({ queryKey: ["articles-retenus"] }),
    client.invalidateQueries({ queryKey: ["articles-resume"] }),
    ...(typoMoved ? [client.resetQueries({ queryKey: ["conferences"] })] : []),
  ]).then(
    () => undefined,
    () => undefined,
  );
}

/**
 * WHAT THE SERVER ALREADY HOLDS FOR THIS PARTICIPANT, and how to write to it.
 *
 * Two chained queries — who am I on this domain, then what am I in this event —
 * because the token answers only the first. They are cached for the visit: coming
 * back through the breadcrumb must not re-ask the server on every step.
 *
 * ⚠️ WHAT COMES FROM HERE IS THE TRUTH; the navigation state is only what is being
 * answered RIGHT NOW. A step therefore reads the answer being given first, and
 * falls back to what was recorded — that is what makes a step reopened later show
 * the choice already made rather than an empty form.
 */
/**
 * Quand relancer une lecture d'identité : une fois, et seulement sur une PANNE (5xx, réseau).
 * Un refus (401/403) ou un « introuvable » est une réponse : on ne la redemande pas.
 */
function retryOnOutage(failures: number, error: unknown): boolean {
  if (failures >= 1) return false;
  return !(error instanceof ApiError) || error.status >= 500;
}

export function useParticipation(
  options: {
    /**
     * ═══ RELIRE À CHAQUE ARRIVÉE ═══ Pour l'écran qui ÉCRIT sans qu'on clique (la
     * confirmation) : il ne doit écrire que sur une participation relue, jamais sur le
     * souvenir d'une participation que le back-office a pu supprimer depuis.
     */
    fresh?: boolean;
  } = {},
) {
  const event = useEvent();
  const client = useQueryClient();
  /**
   * ⚠️ LA SESSION FAIT PARTIE DE LA CLÉ, ET ELLE N'EN FAISAIT PAS PARTIE. `staleTime`
   * infini et `retryOnMount` coupé (voir dessous) figent la réponse pour toute la visite :
   * la question « qui suis-je » posée AVANT la connexion (refusée, ou vide) restait la
   * réponse APRÈS. Mesuré avec la connexion par code : connecté, le site ne montrait
   * toujours pas le profil avant un rechargement. Une session qui s'ouvre ou se ferme est
   * une autre question, donc une autre clé ; les invalidations par préfixe
   * (`["participant"]`) continuent de la couvrir.
   *
   * ⚠️ ET ELLE NE SE RÉSUME PAS À « CONNECTÉ OU NON » — c'est le bug 8 de la chasse du
   * 2026-10-20. Clefée sur un booléen, la requête gardait la même clef quand un autre onglet
   * passait d'A à B : « qui suis-je » répondait B pendant que l'écran gardait les valeurs
   * saisies par A, et l'enregistrement partait sous l'identité de B. La clef porte maintenant
   * l'empreinte du jeton (`auth/session.ts`) : un autre compte est une autre question, donc
   * une autre clef, donc un écran qui se démonte avec ce qu'A y avait tapé.
   */
  const sessionKey = useSessionKey();

  const me = useQuery({
    queryKey: ["participant", event?.domainId, sessionKey],
    queryFn: ({ signal }) => fetchMe(event!.domainId, signal),
    enabled: !!event,
    staleTime: Infinity,
    // A missing account is an answer, not something to keep asking for.
    // ⚠️ UNE PANNE, EN REVANCHE, N'EN EST PAS UNE (bug 9) : un 500 ou un réseau coupé ne dit
    // rien de la personne, et le lire comme « inconnue » la faisait passer pour non inscrite.
    // Une seule relance automatique, puis l'écran d'erreur et son « Réessayer ».
    retry: retryOnOutage,
    /**
     * ⚠️ NI AU MONTAGE D'UN NOUVEL OBSERVATEUR — c'est la suite de la ligne au-dessus, et
     * elle manquait. `staleTime` ne protège que les SUCCÈS : une requête en erreur repart
     * dès qu'un composant de plus l'observe. Or une quinzaine d'écrans appellent ce hook,
     * et chacun qui monte relançait donc un appel dont on connaît déjà la réponse.
     *
     * Mesuré, sur un jeton périmé : 1 819 appels en 6 secondes. Le site se remontait en
     * boucle derrière l'écran de chargement, chaque remontage relançant la requête qui
     * causait le chargement.
     */
    retryOnMount: false,
  });

  const key = ["participation", event?.domainId, event?.eventId, me.data?.id];
  const scope: ParticipationScope | null =
    event && typeof me.data?.id === "number"
      ? {
          domainId: event.domainId,
          eventId: event.eventId,
          userId: me.data.id,
          session: sessionKey,
        }
      : null;

  /**
   * ═══ CHAQUE LECTURE DIT SI LA PARTICIPATION EXISTE ENCORE ═══
   *
   * Absente (`UserEvent: null`, ou un 404), ou sous un autre identifiant que celle qu'on
   * connaissait : le back-office l'a supprimée, et `participationRead` vide tout ce que ce
   * navigateur en gardait (voir `participationGone.ts`). ⚠️ PAS PENDANT UNE ÉCRITURE : une
   * lecture partie avant elle rendrait l'état d'avant, et ferait croire à une suppression.
   */
  const read = async (scope: ParticipationScope, signal?: AbortSignal) => {
    try {
      const found = await fetchParticipation(
        scope.domainId,
        scope.eventId,
        scope.userId,
        signal,
      );
      if (client.isMutating({ mutationKey: ["participation-save"] }) === 0)
        participationRead(client, scope, found.id ?? null);
      return found;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404)
        participationRead(client, scope, null);
      throw error;
    }
  };

  const recorded = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => read(scope!, signal),
    enabled: scope !== null,
    staleTime: Infinity,
    retry: retryOnOutage,
    // Même raison qu'au-dessus.
    retryOnMount: false,
    /**
     * ⚠️ MAIS RELUE AU RETOUR SUR L'ONGLET (participation supprimée, 2026-10-20). Supprimer une
     * participation, c'est passer par l'onglet du back-office : revenir sur celui-ci la
     * relit, et une suppression s'y voit. Jamais pendant une écriture (voir `read`).
     */
    refetchOnWindowFocus: () =>
      client.isMutating({ mutationKey: ["participation-save"] }) === 0
        ? "always"
        : false,
    ...(options.fresh ? { refetchOnMount: "always" as const } : {}),
  });

  /**
   * ═══ LA DERNIÈRE ÉCRITURE A-T-ELLE CHANGÉ QUELQUE CHOSE ? (journal, chasse du 2026-10-20) ═══
   *
   * ⚠️ MESURÉ : un passage complet du tunnel sans rien changer, dans un onglet neuf, ajoutait
   * une action 41. La marque d'avant vivait dans `sessionStorage`, donc par onglet. La réponse
   * vient maintenant de l'écriture elle-même, comparée à ce que le serveur détenait avant elle
   * (`participationChanged`) ; l'appelant la lit pour décider du journal. `undefined` tant
   * qu'aucune écriture de ce composant n'a abouti.
   */
  const lastChange = useRef<boolean | undefined>(undefined);

  const save = useMutation({
    mutationKey: ["participation-save"],
    mutationFn: async (answers: Answers) => {
      /* ⚠️ RIEN NE PART SI LA SESSION A CHANGÉ depuis le rendu de cet écran : les réponses
         sont celles du compte d'avant (bug 8). Voir `auth/session.ts`. */
      assertSession(sessionKey);
      lastChange.current = undefined;
      /* Une relecture partie avant l'écriture rendrait, après elle, l'état d'avant. */
      await client.cancelQueries({ queryKey: key, exact: true });
      /* ⚠️ L'ENREGISTRÉ PÉRIMÉ SE RELIT AVANT D'ÉCRIRE (bug des réponses UE effacées, chasse
         du 2026-10-20). L'étape Informations écrit dans `UserEvents.extended` (fusion côté
         apix) puis marque cette requête périmée SANS la relire (`refetchType: "none"`) ; avec
         `staleTime: Infinity`, `recorded.data` restait celle d'AVANT, et l'écriture qui suit
         (confirmation, retour arrière, `stepSave`) bâtissait son `extended` sur une copie
         vide : api-v1 REMPLACE la colonne, la réponse saisie était effacée. Une lecture qui
         échoue fait échouer l'écriture : mieux vaut « réessayez » qu'une colonne écrasée. */
      const userId = me.data?.id;
      const state = client.getQueryState<Recorded>(key);
      const before =
        event && typeof userId === "number" && (!state || state.isInvalidated || state.data === undefined)
          ? await client.fetchQuery({
              queryKey: key,
              queryFn: ({ signal }) =>
                fetchParticipation(event.domainId, event.eventId, userId, signal),
              staleTime: 0,
            })
          : (recorded.data ?? { responded: false, extended: {} });
      assertSession(sessionKey);
      const orderKey = [
        "articles-retenus",
        event?.domainId,
        event?.eventId,
        me.data?.id,
      ];
      /* Et la commande DÉJÀ ENREGISTRÉE, lue par l'étape billetterie sous cette clé même
         (voir `useArticles`) : identique à celle qu'on s'apprête à écrire, la route n'est
         pas appelée du tout — elle détruirait puis recréerait, à l'identique, des lignes
         déjà réglées. Lue au moment de l'écriture, jamais au rendu : c'est un indice pour
         l'écriture, pas une donnée d'écran.

         ⚠️ ET DEMANDÉE QUAND LE CACHE NE LA CONNAÎT PAS, si une commande doit partir. Mesuré :
         la confirmation rechargée ne l'avait plus, réécrivait une commande identique
         (`PUT …/articles/users/<id>` `{"days":{}}`) et ne pouvait plus dire si quelque chose
         avait changé. Une lecture qui échoue laisse l'écriture faire comme avant. */
      let order = knownOrder(client, orderKey);
      if (
        order === undefined &&
        answers.articles !== undefined &&
        typeof me.data?.id === "number"
      ) {
        const userId = me.data.id;
        order = await client
          .fetchQuery({
            queryKey: orderKey,
            queryFn: ({ signal }) =>
              fetchSelection(event!.domainId, event!.eventId, userId, signal),
            staleTime: Infinity,
          })
          .catch(() => undefined);
      }
      const progress: WriteProgress = { days: false, tickets: false };
      const written = await saveParticipation(
        event!.domainId,
        event!.eventId,
        me.data!.id,
        answers,
        before,
        // Les jours de l'événement voyagent avec son contexte : rien à demander de
        // plus pour savoir lesquels inscrire, et lesquels retirer.
        (event?.days ?? []).map((d) => d.id),
        {
          /* Le catalogue déjà chargé pour l'étape billetterie (une entrée par langue ; les
             identifiants n'en dépendent pas) écarte un article périmé avant l'écriture.
             Absent du cache, on n'ajoute pas de requête : le filtre par jour suffit. */
          known:
            client.getQueryData<Article[]>([
              "articles",
              event?.domainId,
              event?.eventId,
              false,
            ]) ??
            client.getQueryData<Article[]>([
              "articles",
              event?.domainId,
              event?.eventId,
              true,
            ]),
          order,
          session: sessionKey,
        },
        progress,
      );
      lastChange.current = participationChanged(answers, before, progress);
      return written;
    },
    /**
     * What was just written IS what the server holds; re-reading it keeps a step
     * reopened afterwards showing the same thing — et ce qui vient d'être écrit se POSE
     * plutôt que de se redemander. La règle, et ses raisons, vivent dans `settleWrite`.
     *
     * ⚠️ ATTENDUE, ET NON LANCÉE PUIS OUBLIÉE — ce que `onSuccess` REND est attendu par
     * `mutateAsync`, donc l'écriture ne se déclare terminée qu'une fois l'enregistré en
     * place. Sans ça, tout ce qui suit l'écriture affichait l'état d'AVANT le temps d'un
     * aller-retour : le récapitulatif de la confirmation listait le jour qu'on venait de
     * décocher. Un clignotement, mais sur l'écran qui existe pour vérifier.
     */
    onSuccess: (written) => {
      // L'inscription est écrite : le brouillon d'un premier parcours n'a plus d'objet
      // (voir `funnel/draft.ts`).
      if (event && typeof me.data?.id === "number")
        forgetDraft(event.domainId, event.eventId, me.data.id);
      /* ⚠️ LES RÉPONSES DE CETTE ENTRÉE SONT MAINTENANT CELLES DE CETTE PARTICIPATION : son
         empreinte le dit, et une suppression ultérieure les écartera au retour arrière
         (voir `answerSession.ts`). */
      if (scope && written) {
        participationWritten(scope, written.id ?? null);
        restampEntry(sessionKey, written.id ?? null);
      }
      return settleWrite(client, key, written);
    },
  });

  return {
    /** Who is signed in — `undefined` until known. */
    me: me.data as Me | undefined,
    /** `undefined` until it is known — never guessed. */
    recorded: recorded.data as Recorded | undefined,
    /** True while either question is still out. */
    loading: me.isPending || (me.isSuccess && recorded.isPending),
    /**
     * LA QUESTION A ÉTÉ POSÉE ET RÉGLÉE — succès OU échec.
     *
     * ⚠️ CE N'EST PAS L'INVERSE DE `loading`, ET C'EST TOUT L'INTÉRÊT. `isPending`
     * REPART dès qu'une requête est relancée ; `isFetched`, une fois acquis, ne revient
     * jamais en arrière. Ce que ce drapeau existe pour tenir, c'est une GARDE DE RENDU :
     * adossée à `loading`, elle démontait le site à chaque relance, et le remontage
     * relançait la requête — 1 819 appels en 6 secondes, mesurés sur un jeton périmé.
     *
     * ⚠️ RÉGLÉE, PAS RÉUSSIE — la même règle que `ready` dans le fournisseur des
     * réglages : un compte introuvable est une réponse, et attendre un succès tiendrait
     * la page sur l'écran de chargement pour de bon.
     */
    settled: me.isFetched && (!me.isSuccess || recorded.isFetched),
    /** The identity could not be resolved: nothing can be written. */
    unavailable: me.isError || recorded.isError,
    /**
     * ═══ RELIRE CE QUI N'A PAS PU L'ÊTRE (bug 9) ═══
     *
     * Relance la question qui a échoué — « qui suis-je » d'abord ; la participation suit
     * d'elle-même dès que l'identité est connue (sa clef en dépend). Si seule la participation
     * a échoué, c'est elle qu'on relance.
     */
    retryIdentity: () => {
      if (me.isError) void me.refetch();
      else if (recorded.isError) void recorded.refetch();
    },
    /** Une relecture est en cours (le bouton « Réessayer » attend). */
    retrying: me.isFetching || recorded.isFetching,
    /** La participation est en train d'être relue : ce qu'on en a peut être périmé. */
    revalidating: recorded.isFetching,
    /**
     * ═══ LE SERVEUR REFUSE LE JETON ═══
     *
     * ⚠️ SUR CETTE LECTURE-CI, UN 401 NE VEUT DIRE QU'UNE CHOSE. Le client typé prévient
     * que le même statut couvre deux situations opposées — « la session est morte » et
     * « la session est valide mais ce compte n'a pas le droit d'écrire » — et qu'il faut
     * trancher d'après l'ORIGINE, pas d'après le nombre. Ici l'origine lève le doute :
     * `GET /users/me` ne demande aucun droit particulier, elle demande QUI ON EST. Refusée,
     * c'est que le jeton ne vaut plus rien.
     *
     * ⚠️ ET SEULEMENT CELLE-LÀ. Le 401 de `recorded` n'est pas repris : cette route-là
     * porte un événement et un utilisateur, donc son refus peut vouloir dire « pas vos
     * affaires » plutôt que « reconnectez-vous ». Déconnecter là-dessus mettrait quelqu'un
     * dehors pour avoir ouvert la mauvaise adresse.
     */
    sessionRefused: me.error instanceof ApiError && me.error.isUnauthorized,
    save,
    /**
     * La dernière écriture aboutie de ce composant a-t-elle changé quelque chose chez le
     * serveur ? `undefined` quand on ne le sait pas : le journal note alors, comme avant.
     */
    lastWriteChanged: () => lastChange.current,
  };
}
